import { useEffect, useId, useState, type ReactNode } from 'react';
import { Button } from '~/components/ui';

type ToolbarActionButtonProps = {
  disabledReason: string | null | undefined;
  onClick: () => void;
  width?: string;
  children: ReactNode;
};

export function ToolbarActionButton({
  disabledReason,
  onClick,
  width,
  children,
}: Readonly<ToolbarActionButtonProps>) {
  const reasonId = useId();
  const [showReason, setShowReason] = useState(false);
  const disabled = Boolean(disabledReason);

  useEffect(() => {
    if (!showReason) return;
    const dismissOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowReason(false);
    };
    document.addEventListener('keydown', dismissOnEscape);
    return () => document.removeEventListener('keydown', dismissOnEscape);
  }, [showReason]);

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setShowReason(true)}
      onMouseLeave={() => setShowReason(false)}
    >
      <Button
        onClick={() => {
          if (!disabled) onClick();
        }}
        onFocus={() => setShowReason(true)}
        onBlur={() => setShowReason(false)}
        size="sm"
        variant="accent"
        width={width}
        aria-disabled={disabled}
        aria-describedby={disabled ? reasonId : undefined}
        className={disabled ? 'cursor-not-allowed opacity-60' : undefined}
      >
        {children}
      </Button>
      {disabled && (
        <span
          id={reasonId}
          role="tooltip"
          hidden={!showReason}
          className="absolute top-full right-0 z-50 mt-1.5 w-max max-w-64 rounded-md bg-neut-900 px-2.5 py-1.5 text-xs text-neut-0 shadow-md"
        >
          {disabledReason}
        </span>
      )}
    </span>
  );
}
